import { AnalysisItem, AnalysisType, GroupKey, GroupType, projectAnalysis, ProjectAnalysisEvents, UnitType } from "@/types/analysis/ProjectAnalysis";
import { ColorDefinition } from "@/types/colors";
import { ViewerIds } from "@/types/SelectionManager";
import { useProjectData } from "@api/analysis";
import { AppSettingType, FeatureManagementType, MaterialType, ModelData, ScenarioData } from "@api/apiClient";
import DataGrid from "@components/DataGrid/DataGrid";
import useAppStore from "@hooks/useAppStore";
import i18n from "@i18n/i18n";
import SquareIcon from "@mui/icons-material/Square";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import { GridColDef, GridColumnHeaderParams, GridInputRowSelectionModel } from "@mui/x-data-grid-premium";
import sortArrays from "@utils/sortArrays";
import { Emitter, EventType } from "mitt";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ResultsEvent, ResultsEvents } from "../../ResultsEvents";
import MicroChip from "@components/MicroChip/MicroChip";

export interface ByECMaterialsDataGridProps {
  emitter: Emitter<Record<EventType, unknown>>;
}

type MyGridColDef = GridColDef & {
  id?: number;
  groupKey?: GroupKey;
  name?: string;
  scenarioName?: string;
  modelName?: string;
};

type SupplementalTotals = {
  rebarTotalCo2: number;
  nonModelledTotalCo2: number;
};

const EstimatedRebarCo2MaterialTypeId = "E1F8E4B2-6F7C-47AA-9B92-E0D5D2C6B931";
const EstimatedNonModelledCo2MaterialTypeId = "4A1B6230-5B17-4A9F-8A4D-9FC9229F558E";

const ByECMaterialsDataGrid = observer((props: ByECMaterialsDataGridProps) => {
  const appStore = useAppStore();
  const { id: projectId } = useParams();
  const [rows, setRows] = useState<MyGridColDef[]>([]);
  const [columns, setColumns] = useState<MyGridColDef[]>([]);
  const [aggregationModel, setAggregationModel] = useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState<GridInputRowSelectionModel>([]);
  const [rowHighlights, setRowHighlights] = useState<Set<GroupKey>>(new Set());
  const projectData = useProjectData(projectId!);

  useEffect(() => {
    updateGridData();
    updateGridHighlights();
  }, []);

  useEffect(() => {
    projectAnalysis.emitter.on(ProjectAnalysisEvents.ANALYSIS_CHANGED_EVENT, updateGridData);

    return () => {
      projectAnalysis.emitter.off(ProjectAnalysisEvents.ANALYSIS_CHANGED_EVENT, updateGridData);
    };
  }, []);

  useEffect(() => {
    projectAnalysis.emitter.on(ProjectAnalysisEvents.SELECTION_CHANGED_EVENT, updateGridHighlights);

    return () => {
      projectAnalysis.emitter.off(ProjectAnalysisEvents.SELECTION_CHANGED_EVENT, updateGridHighlights);
    };
  }, []);

  useEffect(() => {
    props.emitter.on(ResultsEvents.SELECTION_CHANGED, handleUpdateSelection);

    return () => {
      props.emitter.off(ResultsEvents.SELECTION_CHANGED, handleUpdateSelection);
    };
  }, [rows]);

  useEffect(() => {
    projectAnalysis.emitter.on(ProjectAnalysisEvents.UNIT_SELECTION_CHANGED_EVENT, updateGridData); // Re-calculate grid data with new units

    return () => {
      projectAnalysis.emitter.off(ProjectAnalysisEvents.UNIT_SELECTION_CHANGED_EVENT, updateGridData);
    };
  }, []);

  const handleUpdateSelection = async (event: any) => {
    if (event.key === "geometry-loaded") {
      updateViewers(event.groupKey);
    } else if (event.key !== "data-grid") {
      updateSelection(event.groupKey);
    }
  };

  const updateGridHighlights = async () => {
    const highlights = projectAnalysis.getSelectedElementsByAnalysisType(appStore.selectionManager, projectData.data, AnalysisType.Co2MaterialType) || new Set();
    setRowHighlights(highlights);
  };

  const isModelSelected = (scenarioId: string, modelData: ModelData): boolean => {
    const identifier = modelData.model?.apsViewId || modelData.model?.modelId;
    if (!identifier) {
      return false;
    }

    return appStore.selectionManager.getSelectedModelUrns(scenarioId).includes(identifier);
  };

  const getSupplementalTotalsByEcMaterial = (scenarioData: ScenarioData, groupData: any): Map<string, SupplementalTotals> => {
    const totalsByEcMaterial = new Map<string, SupplementalTotals>();

    scenarioData.modelDatas?.forEach((modelData: ModelData) => {
      if (!isModelSelected(scenarioData.scenarioId!, modelData)) {
        return;
      }

      if (projectAnalysis.groupType === GroupType.Model) {
        const groupModelId = String(groupData.id);
        const modelId = modelData.model?.modelId;
        const modelUrn = modelData.model?.apsViewId;
        const isMatchingModel = modelId === groupModelId || modelUrn === groupModelId;
        if (!isMatchingModel) {
          return;
        }
      }

      modelData.elementDatas?.forEach((elementData) => {
        if ((elementData.componentCount || 0) > 0) {
          return;
        }

        if (!elementData.co2MaterialTypeId) {
          return;
        }

        if (elementData.type !== MaterialType.RebarSupplemental && elementData.type !== MaterialType.NonModelledSupplemental) {
          return;
        }

        const existing = totalsByEcMaterial.get(elementData.co2MaterialTypeId) || { rebarTotalCo2: 0, nonModelledTotalCo2: 0 };
        const totalCo2 = elementData.co2Aggregates?.totalCo2 || 0;

        if (elementData.type === MaterialType.RebarSupplemental) {
          existing.rebarTotalCo2 += totalCo2;
        }

        if (elementData.type === MaterialType.NonModelledSupplemental) {
          existing.nonModelledTotalCo2 += totalCo2;
        }

        totalsByEcMaterial.set(elementData.co2MaterialTypeId, existing);
      });
    });

    return totalsByEcMaterial;
  };

  const updateSelection = (groupKey?: number | string) => {
    if (groupKey) {
      const row = rows.find((r: MyGridColDef) => r.groupKey === groupKey);
      if (row) {
        setRowSelectionModel([row.id!]);
        return;
      }
    }

    setRowSelectionModel([]);
  };

  const renderFormattedNumber = (value?: number) => {
    if (value === undefined || value === null) {
      return "";
    }

    return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
  };

  const updateGridData = async () => {
    const analysisData = projectAnalysis.getAnalysis(AnalysisType.Co2MaterialType);
    if (analysisData) {
      const aggregationModel: any = {};
      const keys = analysisData.getGroupKeys().filter(
        (key) => key !== EstimatedRebarCo2MaterialTypeId && key !== EstimatedNonModelledCo2MaterialTypeId,
      );
      let newRows = keys.map((key, index) => ({ id: index, groupKey: key, name: "" })) as MyGridColDef[];
      let s = 1;
      let i = 1;
      const newColDefs: MyGridColDef[] = [...colDefs];
      analysisData.scenarioAnalyses.forEach((scenarioAnalysis) => {
        const scenarioData = projectData.data?.scenarioDatas?.find((data) => data.scenarioId === scenarioAnalysis.scenarioId);
        scenarioAnalysis.groupData.forEach((groupData) => {
          const groupItemsByKey = new Map(Array.from(groupData.items.values()).map((item: AnalysisItem) => [String(item.groupKey), item]));
          const supplementalTotalsByEcMaterial = scenarioData ? getSupplementalTotalsByEcMaterial(scenarioData, groupData) : new Map<string, SupplementalTotals>();

          s = scenarioAnalysis.scenarioIndex + 1;
          newColDefs.push({
            field: `col${i}`,
            headerName: `${i18n.t("results-tab.title.quantity")}`,
            flex: 1,
            type: "number",
            minWidth: 100,
          });
          aggregationModel[`col${i}`] = "sum";
          newRows = newRows.map((row) => {
            const item = groupItemsByKey.get(String(row.groupKey));
            return {
              ...row,
              [`col${i}`]: item?.elementCount || 0,
            };
          });
          i++;

          newColDefs.push({
            field: `col${i}`,
            scenarioName: scenarioAnalysis.name,
            modelName: groupData.name,
            headerName: i18n.t("analysis.virtual-title.estimated-rebar", { defaultValue: "Estimated Rebar" }),
            renderHeader,
            flex: 1,
            type: "number",
            minWidth: 170,
            valueFormatter: renderFormattedNumber,
          });
          aggregationModel[`col${i}`] = "sum";
          newRows = newRows.map((row) => {
            const totals = supplementalTotalsByEcMaterial.get(String(row.groupKey));
            let convertedValue = totals?.rebarTotalCo2 || 0;
            if (appStore.hasFeature(FeatureManagementType.AdditionalMetadata)) {
              convertedValue = projectAnalysis.applyUnitConversion(convertedValue, scenarioAnalysis);
            }
            return {
              ...row,
              [`col${i}`]: convertedValue,
            };
          });
          i++;

          newColDefs.push({
            field: `col${i}`,
            scenarioName: scenarioAnalysis.name,
            modelName: groupData.name,
            headerName: i18n.t("analysis.virtual-title.estimated-non-modelled", { defaultValue: "Estimated Non-Modelled" }),
            renderHeader,
            flex: 1,
            type: "number",
            minWidth: 210,
            valueFormatter: renderFormattedNumber,
          });
          aggregationModel[`col${i}`] = "sum";
          newRows = newRows.map((row) => {
            const totals = supplementalTotalsByEcMaterial.get(String(row.groupKey));
            let convertedValue = totals?.nonModelledTotalCo2 || 0;
            if (appStore.hasFeature(FeatureManagementType.AdditionalMetadata)) {
              convertedValue = projectAnalysis.applyUnitConversion(convertedValue, scenarioAnalysis);
            }
            return {
              ...row,
              [`col${i}`]: convertedValue,
            };
          });
          i++;

          newColDefs.push({
            field: `col${i}`,
            scenarioName: scenarioAnalysis.name,
            modelName: groupData.name,
            headerName: projectAnalysis.groupType === GroupType.Model ? `S${s}-M${groupData.groupIndex + 1}` : `S${s}`,
            renderHeader,
            flex: 1,
            type: "number",
            minWidth: 150,
            valueFormatter: renderFormattedNumber,
          });
          aggregationModel[`col${i}`] = "sum";
          newRows = newRows.map((row) => {
            const item = groupItemsByKey.get(String(row.groupKey));
            let convertedValue = item?.co2eTotal || 0;

            // Apply unit conversion if AdditionalMetadata feature is enabled
            if (appStore.hasFeature(FeatureManagementType.AdditionalMetadata)) {
              convertedValue = projectAnalysis.applyUnitConversion(convertedValue, scenarioAnalysis);
            }

            return {
              ...row,
              name: item?.name || row.name,
              [`col${i}`]: convertedValue,
            };
          });
          i++;
        });
        s++;
      });

      // Sort newRows by id in descending order
      const gridColors = analysisData.colorDefinitions();
      const sorted = sortArrays({ newRows, gridColors }, (a, b) => b.id! - a.id!) as { newRows: MyGridColDef[]; gridColors: ColorDefinition[] };
      newRows = sorted.newRows.map((value: MyGridColDef, index: number) => ({ ...value, color: sorted.gridColors[index].hex }));

      setColumns([...newColDefs]);
      setRows(newRows);
      setAggregationModel(aggregationModel);
    } else {
      setRows([]);
      setAggregationModel({});
    }
  };

  const renderHeader = (params: GridColumnHeaderParams) => {
    const { colDef }: { colDef: MyGridColDef } = params;

    const getUnitDisplay = () => {
      const unitKey = "results-tab.title.co2e"; //Default unit key
      if (appStore.hasFeature(FeatureManagementType.AdditionalMetadata)) {
        switch (projectAnalysis.selectedUnitType) {
          case UnitType.KgCO2ePerM2:
            return i18n.t("results-tab.title.unit-kgco2e-m2");
          case UnitType.KgCO2ePerM2PerYear:
            return i18n.t("results-tab.title.unit-kgco2e-m2-year");
          case UnitType.tCO2e:
            return i18n.t(unitKey);
        }
      }
      return i18n.t(unitKey);
    };
   
    return (
      <Tooltip
        title={
          <>
            <Typography variant="caption">{colDef.scenarioName}</Typography>
            {projectAnalysis.groupType === GroupType.Model && <Typography variant="subtitle1">{colDef.modelName}</Typography>}
          </>
        }
      >
        <Box>
          <MicroChip label={`${params.colDef.headerName}`} />
          <Box ml={1} component={"span"}>
            {getUnitDisplay()}
          </Box>
        </Box>
      </Tooltip>
    );
  };

  const colDefs: MyGridColDef[] = [
    {
      field: "name",
      headerName: i18n.t("results-tab.title.co2-material-types"),
      flex: 1,
      minWidth: 200,
      renderCell: (params: any) => {
        if (params.id === "auto-generated-group-footer-root") {
          return <></>;
        }

        return (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <SquareIcon sx={{ color: params.row.color }} />
            <Box ml={1}>{params.row.name}</Box>
          </Box>
        );
      },
    },
  ];

  const updateViewers = async (groupKey?: GroupKey | undefined) => {
    if (groupKey) {
      projectAnalysis.getAnalysis(AnalysisType.Co2MaterialType)?.scenarioAnalyses.forEach((scenarioAnalysis) => {
        scenarioAnalysis.modelGroupCadIdsMap.forEach((groupCadIds, urn) => {
          (async () => {
            const viewerId = appStore.selectionManager.getViewerIdByScenarioId(scenarioAnalysis.scenarioId)!;
            if (viewerId === ViewerIds.Viewer1) {
              await appStore.lmViewer1?.isolateByCadIds(groupCadIds.get(groupKey) || [], urn);
            } else {
              await appStore.lmViewer2?.isolateByCadIds(groupCadIds.get(groupKey) || [], urn);
            }
          })();
        });
      });
    } else {
      await appStore.lmViewer1?.resetView();
      await appStore.lmViewer2?.resetView();
    }
  };

  const handleRowSelection = async (colIndex?: number | undefined, groupKey?: GroupKey | undefined) => {
    console.log(`updateViewers: ${colIndex}, ${groupKey}`);

    await updateViewers(groupKey);

    const name = rows.find((r: MyGridColDef) => r.groupKey === groupKey)?.name;

    const newEvent: ResultsEvent = {
      key: "data-grid",
      eventType: ResultsEvents.SELECTION_CHANGED,
      groupKey: name,
    };

    props.emitter.emit(ResultsEvents.SELECTION_CHANGED, newEvent);
  };

  return (
    <Box sx={{ display: "flex", flexGrow: 1, flexShrink: 0, position: "relative", height: "100%", width: "100%" }}>
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row: any) => row.id}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "calc(100% - 8px)",
          height: "100%",
        }}
        getRowClassName={(params) => (rowHighlights.has(params.row.groupKey) ? "app-theme--active" : "")}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={(selection) => {
          setRowSelectionModel(selection);
          if (selection.length > 0) {
            const row = rows.find((r: any) => r.id === selection[0]);
            handleRowSelection(row?.id, row?.groupKey);
          }
        }}
        aggregationModel={aggregationModel}
      />
    </Box>
  );
});

export default ByECMaterialsDataGrid;
